import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { ADMIN_ROLES } from "../../../../utils/adminSession";
import {
  GLOBAL_TENANT_ASSIGNMENT,
  GLOBAL_TENANT_ID,
  SUPER_ADMIN_ROLE_OPTIONS,
  SUPER_ADMIN_STATUS_OPTIONS,
} from "../../../../services/superAdmin/constants";

const buildInitialState = (admin) => ({
  fullName: admin?.fullName || "",
  email: admin?.email || "",
  password: "",
  phoneNumber: admin?.phoneNumber || "",
  role: admin?.role || ADMIN_ROLES.ADMIN,
  status: admin?.status || "active",
  assignedTenantId:
    admin?.assignedTenantId ||
    (admin?.role === ADMIN_ROLES.SUPER_ADMIN ? GLOBAL_TENANT_ID : ""),
});

const EMPTY_TENANT_FORM = {
  tenantName: "",
  appName: "",
  instituteName: "",
  code: "",
};

const WhiteLabelAdminDrawer = ({
  admin,
  isCreatingTenant,
  isLoading,
  isOpen,
  onCreateTenant,
  onClose,
  onSubmit,
  tenants = [],
}) => {
  const [values, setValues] = useState(buildInitialState(admin));
  const [errors, setErrors] = useState({});
  const [tenantValues, setTenantValues] = useState(EMPTY_TENANT_FORM);
  const [tenantErrors, setTenantErrors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setValues(buildInitialState(admin));
    setErrors({});
    setTenantValues(EMPTY_TENANT_FORM);
    setTenantErrors({});
  }, [admin, isOpen]);

  const selectedTenant = useMemo(() => {
    if (values.role === ADMIN_ROLES.SUPER_ADMIN) {
      return GLOBAL_TENANT_ASSIGNMENT;
    }

    return tenants.find((tenant) => tenant.id === values.assignedTenantId) || null;
  }, [tenants, values.assignedTenantId, values.role]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((currentValues) => {
      if (name === "role") {
        const nextRole = value;
        return {
          ...currentValues,
          role: nextRole,
          assignedTenantId:
            nextRole === ADMIN_ROLES.SUPER_ADMIN
              ? GLOBAL_TENANT_ID
              : currentValues.assignedTenantId === GLOBAL_TENANT_ID
                ? tenants[0]?.id || ""
                : currentValues.assignedTenantId,
        };
      }

      return {
        ...currentValues,
        [name]: value,
      };
    });

    setErrors((currentErrors) => ({
      ...currentErrors,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const nextErrors = {};
    const normalizedPhoneNumber = String(values.phoneNumber).replace(/\D/g, "");

    if (!values.fullName.trim()) {
      nextErrors.fullName = "Full name is required.";
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!admin && !values.password.trim()) {
      nextErrors.password = "Password is required.";
    }

    if (values.password && values.password.length < 8) {
      nextErrors.password = "Password must contain at least 8 characters.";
    }

    if (normalizedPhoneNumber.length < 10 || normalizedPhoneNumber.length > 15) {
      nextErrors.phoneNumber = "Phone number must contain 10 to 15 digits.";
    }

    if (
      values.role !== ADMIN_ROLES.SUPER_ADMIN &&
      !values.assignedTenantId.trim()
    ) {
      nextErrors.assignedTenantId = "Select a tenant/app/institute assignment.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleTenantChange = (event) => {
    const { name, value } = event.target;

    setTenantValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
    setTenantErrors((currentErrors) => ({
      ...currentErrors,
      [name]: "",
    }));
  };

  const validateTenantForm = () => {
    const nextErrors = {};

    if (!tenantValues.tenantName.trim()) {
      nextErrors.tenantName = "Tenant name is required.";
    }

    if (!tenantValues.appName.trim()) {
      nextErrors.appName = "App name is required.";
    }

    if (!tenantValues.instituteName.trim()) {
      nextErrors.instituteName = "Institute name is required.";
    }

    setTenantErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCreateTenant = async () => {
    if (!validateTenantForm()) {
      return;
    }

    let createdTenant = null;

    try {
      createdTenant = await onCreateTenant({
        tenantName: tenantValues.tenantName.trim(),
        appName: tenantValues.appName.trim(),
        instituteName: tenantValues.instituteName.trim(),
        code: tenantValues.code.trim(),
        status: "active",
      });
    } catch (error) {
      return;
    }

    if (!createdTenant) {
      return;
    }

    setValues((currentValues) => ({
      ...currentValues,
      assignedTenantId: createdTenant.id,
    }));
    setTenantValues(EMPTY_TENANT_FORM);
    setTenantErrors({});
    setErrors((currentErrors) => ({
      ...currentErrors,
      assignedTenantId: "",
    }));
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    onSubmit({
      ...values,
      fullName: values.fullName.trim(),
      email: values.email.trim().toLowerCase(),
      ...(values.password ? { password: values.password } : {}),
      phoneNumber: values.phoneNumber.replace(/\D/g, ""),
      assignedTenantId:
        values.role === ADMIN_ROLES.SUPER_ADMIN
          ? GLOBAL_TENANT_ID
          : values.assignedTenantId,
    });
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={isLoading ? () => {} : onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton disabled={isLoading} />
        <DrawerHeader>{admin ? "Edit Admin" : "Create Admin"}</DrawerHeader>

        <DrawerBody>
          <div className="space-y-5">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isInvalid={Boolean(errors.fullName)}>
                <FormLabel>Full Name</FormLabel>
                <Input
                  name="fullName"
                  placeholder="Enter full name"
                  value={values.fullName}
                  onChange={handleChange}
                />
                <FormErrorMessage>{errors.fullName}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={Boolean(errors.email)}>
                <FormLabel>Email</FormLabel>
                <Input
                  name="email"
                  placeholder="admin@example.com"
                  value={values.email}
                  onChange={handleChange}
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={Boolean(errors.phoneNumber)}>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  name="phoneNumber"
                  placeholder="9876543210"
                  value={values.phoneNumber}
                  onChange={handleChange}
                />
                <FormErrorMessage>{errors.phoneNumber}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={Boolean(errors.password)}>
                <FormLabel>Password</FormLabel>
                <Input
                  name="password"
                  type="password"
                  placeholder={admin ? "Leave blank to keep current password" : "Enter password"}
                  value={values.password}
                  onChange={handleChange}
                />
                <FormErrorMessage>{errors.password}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Role</FormLabel>
                <Select name="role" value={values.role} onChange={handleChange}>
                  {SUPER_ADMIN_ROLE_OPTIONS.map((roleOption) => (
                    <option key={roleOption.value} value={roleOption.value}>
                      {roleOption.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select name="status" value={values.status} onChange={handleChange}>
                  {SUPER_ADMIN_STATUS_OPTIONS.map((statusOption) => (
                    <option key={statusOption.value} value={statusOption.value}>
                      {statusOption.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isInvalid={Boolean(errors.assignedTenantId)}>
                <FormLabel>Assigned Tenant/App/Institute</FormLabel>
                <Select
                  name="assignedTenantId"
                  value={values.role === ADMIN_ROLES.SUPER_ADMIN ? GLOBAL_TENANT_ID : values.assignedTenantId}
                  onChange={handleChange}
                  isDisabled={values.role === ADMIN_ROLES.SUPER_ADMIN}
                >
                  <option value="">
                    {tenants.length ? "Select assignment" : "No tenants available"}
                  </option>
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.tenantName} | {tenant.appName}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.assignedTenantId}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>

            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <Text className="text-sm font-semibold text-gray-800">
                Add Tenant/App/Institute To Dropdown
              </Text>
              <Text className="mt-1 text-xs text-gray-500">
                Use these fields when the assignment you need is not available in the dropdown yet.
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} className="mt-4">
                <FormControl isInvalid={Boolean(tenantErrors.tenantName)}>
                  <FormLabel>Tenant Name</FormLabel>
                  <Input
                    name="tenantName"
                    placeholder="Enter tenant name"
                    value={tenantValues.tenantName}
                    onChange={handleTenantChange}
                  />
                  <FormErrorMessage>{tenantErrors.tenantName}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(tenantErrors.appName)}>
                  <FormLabel>App Name</FormLabel>
                  <Input
                    name="appName"
                    placeholder="Enter app name"
                    value={tenantValues.appName}
                    onChange={handleTenantChange}
                  />
                  <FormErrorMessage>{tenantErrors.appName}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={Boolean(tenantErrors.instituteName)}>
                  <FormLabel>Institute Name</FormLabel>
                  <Input
                    name="instituteName"
                    placeholder="Enter institute name"
                    value={tenantValues.instituteName}
                    onChange={handleTenantChange}
                  />
                  <FormErrorMessage>{tenantErrors.instituteName}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Tenant Code</FormLabel>
                  <Input
                    name="code"
                    placeholder="Optional code, e.g. ADH-CENTRAL"
                    value={tenantValues.code}
                    onChange={handleTenantChange}
                  />
                </FormControl>
              </SimpleGrid>

              <div className="mt-4 flex justify-end">
                <Button
                  colorScheme="purple"
                  variant="outline"
                  onClick={handleCreateTenant}
                  isLoading={isCreatingTenant}
                >
                  Add To Dropdown
                </Button>
              </div>
            </div>

            <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
              <Text className="text-sm font-semibold text-gray-800">
                Assignment Preview
              </Text>
              <Text className="mt-3 text-sm text-gray-600">
                Tenant: {selectedTenant?.tenantName || "Not selected"}
              </Text>
              <Text className="mt-1 text-sm text-gray-600">
                App: {selectedTenant?.appName || "Not selected"}
              </Text>
              <Text className="mt-1 text-sm text-gray-600">
                Institute: {selectedTenant?.instituteName || "Not selected"}
              </Text>
              <Text className="mt-3 text-xs text-gray-500">
                Super Admin accounts automatically inherit access to every tenant.
              </Text>
            </div>
          </div>
        </DrawerBody>

        <DrawerFooter>
          <Button mr={3} onClick={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button colorScheme="purple" onClick={handleSubmit} isLoading={isLoading}>
            {admin ? "Save Changes" : "Create Admin"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default WhiteLabelAdminDrawer;
